/**
 * Player Lobby Component
 *
 * Waiting screen for players before the game starts.
 */

interface PlayerLobbyProps {
  gamePin: string;
  nickname: string;
  participantCount: number;
  isReady?: boolean;
  onReady: () => void;
  onLeave?: () => void;
}

export function PlayerLobby({
  gamePin,
  nickname,
  participantCount,
  isReady = false,
  onReady,
  onLeave,
}: PlayerLobbyProps) {
  return (
    <div className="text-center max-w-sm w-full">
      {/* Game PIN */}
      <div className="mb-8">
        <p className="text-sm text-purple-300 uppercase tracking-wide mb-2">Game PIN</p>
        <p className="text-5xl font-mono font-bold text-white tracking-[0.3em]">{gamePin}</p>
      </div>

      {/* Welcome */}
      <div className="mb-8">
        <p className="text-purple-300 mb-2">Welcome,</p>
        <p className="text-3xl font-bold text-white">{nickname}</p>
      </div>

      {/* Waiting Animation */}
      <div className="mb-8">
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 rounded-full border-4 border-purple-500/20" />
          <div className="absolute inset-0 rounded-full border-4 border-purple-400 border-t-transparent animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-10 h-10 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Status */}
      <p className="text-xl text-white mb-2">Waiting for host to start...</p>
      <p className="text-purple-300">
        {participantCount} {participantCount === 1 ? 'player' : 'players'} in lobby
      </p>

      {/* Ready Button */}
      {isReady ? (
        <div className="mt-8 px-8 py-4 bg-green-600/50 border-2 border-green-400 text-white font-bold rounded-xl flex items-center justify-center gap-3">
          <svg className="w-6 h-6 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span>Ready! Waiting for host...</span>
        </div>
      ) : (
        <button
          onClick={onReady}
          className="mt-8 px-8 py-4 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition-colors transform hover:scale-105"
        >
          I'm Ready!
        </button>
      )}

      {/* Leave Button */}
      {onLeave && (
        <button
          onClick={onLeave}
          className="mt-4 text-sm text-purple-400 hover:text-purple-300 transition-colors"
        >
          Leave Game
        </button>
      )}

      {/* Tips */}
      <div className="mt-12 p-4 bg-white/5 rounded-xl text-left">
        <h3 className="text-xs font-semibold text-purple-300 uppercase tracking-wide mb-2">
          Tips
        </h3>
        <ul className="text-sm text-purple-400 space-y-1">
          <li>- Keep your device screen on</li>
          <li>- Answer quickly for more points</li>
          <li>- Streak bonuses for consecutive correct answers!</li>
        </ul>
      </div>
    </div>
  );
}

export default PlayerLobby;
